import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Loader2, Star, MapPin, Briefcase, CheckCircle2, Phone, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AssignProviderDialog = ({ open, onOpenChange, request, onAssigned }) => {
  const [providers, setProviders] = useState([]);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const fetchProviders = async () => {
    if (!request) return;
    setIsFetching(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/roadside/admin/providers-for-assignment`,
        { params: { request_number: request.request_number } }
      );
      setProviders(res.data.providers || []);
    } catch (err) {
      toast.error('Failed to load mechanics');
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (open && request) {
      setSelectedProviderId(null);
      fetchProviders();
    }
  }, [open, request?.request_number]);

  const handleAssign = async () => {
    if (!selectedProviderId) {
      toast.error('Please select a mechanic first');
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/roadside/admin/assign-provider`,
        {
          request_number: request.request_number,
          provider_id: selectedProviderId,
        }
      );
      toast.success(res.data.message || 'Mechanic assigned');
      onAssigned?.(res.data.reservation);
      onOpenChange(false);
    } catch (err) {
      toast.error('Failed to assign mechanic', {
        description: err.response?.data?.detail || 'Please try again',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="assign-provider-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl">Assign Service Provider</DialogTitle>
          <DialogDescription>
            {request ? (
              <>
                Reservation <span className="font-semibold">#{request.request_number}</span>
                {' · '}{request.service_type}
              </>
            ) : 'Select a mechanic to dispatch'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end -mt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchProviders}
            disabled={isFetching}
            data-testid="refresh-providers-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="space-y-3" data-testid="provider-list">
          {isFetching && providers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading mechanics...
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No mechanics registered yet.
            </div>
          ) : (
            providers.map((p) => {
              const isSelected = selectedProviderId === p.provider_id;
              return (
                <Card
                  key={p.provider_id}
                  data-testid={`provider-card-${p.provider_id}`}
                  onClick={() => setSelectedProviderId(p.provider_id)}
                  className={`p-4 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-2 border-red-500 bg-red-50'
                      : 'border hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-gray-900">{p.name}</h4>
                        {p.is_available ? (
                          <Badge className="bg-green-100 text-green-800 border-green-300 border">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5 inline-block animate-pulse" />
                            Online
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600 border-gray-300 border">
                            <span className="w-2 h-2 bg-gray-400 rounded-full mr-1.5 inline-block" />
                            Offline
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-3.5 h-3.5 text-gray-500" />
                          <span>{p.active_jobs} active</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5 text-gray-500" />
                          <span>{p.completed_jobs} completed</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                          <span>{(p.rating || 5.0).toFixed(1)} rating</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-gray-500" />
                          <span>
                            {p.distance_miles != null
                              ? `${p.distance_miles} mi away`
                              : 'Distance N/A'}
                          </span>
                        </div>
                      </div>

                      {p.phone && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                          <Phone className="w-3 h-3" />
                          {p.phone}
                        </div>
                      )}
                    </div>

                    {isSelected && (
                      <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            data-testid="assign-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={!selectedProviderId || isLoading}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="assign-confirm-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              'Assign Job'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
